import React, { useState, useEffect } from 'react';
import { Eye, EyeOff, X } from 'lucide-react';
import './css/ChangePasswordModal.css';

const ChangePasswordModal = ({ isOpen, onClose, user }) => {
  const [formData, setFormData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Reset form when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      // Reset form when modal opens
      setFormData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });
      setError('');
      setSuccess('');
      setShowPasswords({
        current: false,
        new: false,
        confirm: false
      });
    }
  }, [isOpen]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error when user starts typing
    if (error) setError('');
    if (success) setSuccess('');
  };

  const togglePasswordVisibility = (field) => {
    setShowPasswords(prev => ({
      ...prev,
      [field]: !prev[field]
    }));
  };

  const validateForm = () => {
    if (!formData.currentPassword) {
      setError('Current password is required');
      return false;
    }
    if (!formData.newPassword) {
      setError('New password is required');
      return false;
    }
    if (formData.newPassword.length < 3) {
      setError('New password must be at least 3 characters long');
      return false;
    }
    if (formData.newPassword === formData.currentPassword) {
      setError('New password must be different from current password');
      return false;
    }
    if (formData.newPassword !== formData.confirmPassword) {
      setError('New password and confirm password do not match');
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    setLoading(true);
    setError('');

    try {
      const response = await fetch('/api/changePassword/changepassword', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          pkkey: user.pkkey,
          currentPassword: formData.currentPassword,
          newPassword: formData.newPassword
        }),
      });

      const data = await response.json();

      if (data.success) {
        setSuccess('Password changed successfully!');
        
        // Clear form immediately after success
        /*
        setFormData({
          currentPassword: '',
          newPassword: '',
          confirmPassword: ''
        });
        */

        // Hide password toggles
        setShowPasswords({
          current: false,
          new: false,
          confirm: false
        });
        
        // Auto-close modal after 2 seconds
        setTimeout(() => {
          handleClose();
        }, 2000);
      } else {
        setError(data.message || 'Failed to change password');
      }
    } catch (error) {
      console.error('Error changing password:', error);
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    // Reset all states when closing
    setFormData({
      currentPassword: '',
      newPassword: '',
      confirmPassword: ''
    });
    setError('');
    setSuccess('');
    setShowPasswords({
      current: false,
      new: false,
      confirm: false
    });
    setLoading(false);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="cpw-overlay">
      <div className="cpw-container">
        <div className="cpw-header">
          <h2 className="cpw-title">Change Password</h2>
          <button 
            onClick={handleClose}
            className="cpw-close-btn"
            disabled={loading}
          >
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="cpw-form">
          {error && (
            <div className="cpw-alert cpw-alert-error">
              {error}
            </div>
          )}
          
          {success && (
            <div className="cpw-alert cpw-alert-success">
              {success}
            </div>
          )}

          <div className="cpw-form-group">
            <label className="cpw-label">Current Password</label>
            <div className="cpw-input-wrap">
              <input
                type={showPasswords.current ? 'text' : 'password'}
                name="currentPassword"
                value={formData.currentPassword}
                onChange={handleInputChange}
                className="cpw-input"
                disabled={loading || success}
                required
              />
              <button
                type="button"
                onClick={() => togglePasswordVisibility('current')}
                className="cpw-toggle-btn"
                disabled={loading || success}
              >
                {showPasswords.current ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
          </div>

          <div className="cpw-form-group">
            <label className="cpw-label">New Password</label>
            <div className="cpw-input-wrap">
              <input
                type={showPasswords.new ? 'text' : 'password'}
                name="newPassword"
                value={formData.newPassword}
                onChange={handleInputChange}
                className="cpw-input"
                minLength="3"
                disabled={loading || success}
                required
              />
              <button
                type="button"
                onClick={() => togglePasswordVisibility('new')}
                className="cpw-toggle-btn"
                disabled={loading || success}
              >
                {showPasswords.new ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
            <p className="cpw-hint">Password must be at least 3 characters long</p>
          </div>

          <div className="cpw-form-group">
            <label className="cpw-label">Confirm New Password</label>
            <div className="cpw-input-wrap">
              <input
                type={showPasswords.confirm ? 'text' : 'password'}
                name="confirmPassword"
                value={formData.confirmPassword}
                onChange={handleInputChange}
                className="cpw-input"
                disabled={loading || success}
                required
              />
              <button
                type="button"
                onClick={() => togglePasswordVisibility('confirm')}
                className="cpw-toggle-btn"
                disabled={loading || success}
              >
                {showPasswords.confirm ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
          </div>

          <div className="cpw-actions">
            <button
              type="button"
              onClick={handleClose}
              className="cpw-btn cpw-btn-secondary"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="cpw-btn cpw-btn-primary"
              disabled={loading || success}
            >
              {loading ? 'Changing...' : success ? 'Success!' : 'Change Password'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ChangePasswordModal;